<script setup lang="ts">
/**
 * One section of a sidebar's action column: a heading and the actions below
 * it. With `collapsible` the heading becomes a toggle button (`aria-expanded`,
 * `aria-controls`) that emits `toggle` — the open state stays with the caller,
 * which hides the body with `hideBody`. `hideHeader` drops the heading for a
 * section that stands on its own.
 *
 * The kit version of the ERP's `actions/Section.vue`, with the same props,
 * slots and event.
 */
import { useId } from 'vue';

withDefaults(
  defineProps<{
    labelText: string;
    hideHeader?: boolean;
    hideBody?: boolean;
    /** Heading becomes a toggle button emitting `toggle`. */
    collapsible?: boolean;
  }>(),
  { hideHeader: false, hideBody: false, collapsible: false }
);

const emit = defineEmits<{ toggle: [] }>();

defineSlots<{
  default?: () => unknown;
  /** Trailing content of the heading (a chevron, a count). */
  header?: () => unknown;
}>();

const bodyId = useId();
</script>

<template>
  <div class="nx-action-section">
    <button
      v-if="!hideHeader && collapsible"
      type="button"
      class="nx-action-section__head is-toggle"
      :aria-expanded="!hideBody"
      :aria-controls="bodyId"
      @click="emit('toggle')"
    >
      <span class="nx-action-section__label">{{ labelText }}</span>
      <span v-if="$slots.header" class="nx-action-section__extra"><slot name="header" /></span>
    </button>
    <div v-else-if="!hideHeader" class="nx-action-section__head">
      <span class="nx-action-section__label">{{ labelText }}</span>
      <span v-if="$slots.header" class="nx-action-section__extra"><slot name="header" /></span>
    </div>
    <div v-if="!hideBody" :id="bodyId" class="nx-action-section__body">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.nx-action-section {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-block: var(--nxt-space-2xs);
}
.nx-action-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nxt-space-xs);
  padding-block: var(--nxt-space-2xs);
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-action-section__head.is-toggle {
  width: 100%;
  padding-inline: 0;
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: none;
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  text-align: start;
  cursor: pointer;
  transition: color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-action-section__head.is-toggle:hover {
  color: var(--nxt-fg);
}
.nx-action-section__head.is-toggle:focus-visible {
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-action-section__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-action-section__extra {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
}
.nx-action-section__body {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  padding-block: var(--nxt-space-xs);
  font-size: var(--nxt-font-size-xs);
}
@media (prefers-reduced-motion: reduce) {
  .nx-action-section__head.is-toggle {
    transition: none;
  }
}
</style>
